import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { AfsolSectionCard } from "@/components/afsol/AfsolSectionCard";
import { AfsolPartyPanel } from "@/components/afsol/AfsolPartyPanel";
import { AfsolPrintDocument } from "@/components/afsol/AfsolPrintDocument";
import { API, axios } from "@/utils/constants";
import {
  ACCESSORY_FIELDS,
  ATTACHMENT_FIELDS,
  applyLoanToAfsolForm,
  applyVehicleLookupToForm,
  createDefaultAfsolForm,
  FINANCING_SOURCE_OPTIONS,
  formatReferenceNumber,
  hydrateAfsolForm,
  INSURANCE_OPTIONS,
  normalizeFinancingSources,
  normalizeIsoDate,
  personLookupToPartyFields,
} from "@/utils/afsol";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, CarFront, Loader2, Save, Search } from "lucide-react";
import { toast } from "sonner";

const SectionInput = ({ label, value, onChange, testId, type = "text", readOnly = false }) => (
  <div className="space-y-1">
    <Label htmlFor={testId} className="text-[11px] font-medium text-slate-600">{label}</Label>
    <Input
      id={testId}
      type={type}
      value={value || ""}
      onChange={(event) => onChange(event.target.value)}
      readOnly={readOnly}
      className="h-9 rounded-sm border-slate-300 text-sm"
      data-testid={testId}
    />
  </div>
);

export const AfsolFormPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { afsalId } = useParams();
  const { user } = useAuth();
  const defaultCreatedBy = user?.full_name && user.full_name !== "FONDO Administrator" ? user.full_name : "Javier Enrique";

  const [form, setForm] = useState(() => createDefaultAfsolForm(defaultCreatedBy));
  const [recordId, setRecordId] = useState(afsalId || "");
  const [loading, setLoading] = useState(Boolean(afsalId));
  const [saving, setSaving] = useState(false);
  const [vehicleLookupLoading, setVehicleLookupLoading] = useState(false);
  const [vehicleLookupFailed, setVehicleLookupFailed] = useState(false);
  const [matchedLoanText, setMatchedLoanText] = useState("");
  const [lookupLoadingMap, setLookupLoadingMap] = useState({});
  const [financingSelection, setFinancingSelection] = useState({
    sellerFinancingNotes: "",
    buyerFinancingNotes: "",
  });
  const autoPrintHandledRef = useRef(false);

  useEffect(() => {
    if (!afsalId) {
      setForm(createDefaultAfsolForm(defaultCreatedBy));
      setLoading(false);
      return;
    }

    const fetchAfsal = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/afsals/${afsalId}`);
        setRecordId(response.data.id);
        setForm(hydrateAfsolForm(response.data.form_data, defaultCreatedBy));
      } catch (error) {
        toast.error("Afsal fannst ekki");
        navigate("/afsol");
      } finally {
        setLoading(false);
      }
    };

    fetchAfsal();
  }, [afsalId, defaultCreatedBy, navigate]);

  const pageTitle = useMemo(
    () => (recordId ? formatReferenceNumber(form.general.referenceNumber) : "Nýtt afsal"),
    [form.general.referenceNumber, recordId],
  );

  const updateSection = (section, field, value) => {
    setForm((current) => ({
      ...current,
      [section]: {
        ...current[section],
        [field]: value,
      },
    }));
  };

  const updateNestedGroup = (group, key, value) => {
    setForm((current) => ({
      ...current,
      [group]: {
        ...current[group],
        [key]: value,
      },
    }));
  };

  const updatePaymentBreakdown = (index, value) => {
    setForm((current) => ({
      ...current,
      payment: {
        ...current.payment,
        breakdown: current.payment.breakdown.map((item, itemIndex) => (
          itemIndex === index ? { ...item, amount: value } : item
        )),
      },
    }));
  };

  const addFinancingSource = (field, value) => {
    if (!value) return;

    setForm((current) => {
      const existing = normalizeFinancingSources(current.payment[field]);
      if (existing.includes(value)) return current;

      return {
        ...current,
        payment: {
          ...current.payment,
          [field]: [...existing, value],
        },
      };
    });

    setFinancingSelection((current) => ({ ...current, [field]: "" }));
  };

  const removeFinancingSource = (field, value) => {
    setForm((current) => ({
      ...current,
      payment: {
        ...current.payment,
        [field]: normalizeFinancingSources(current.payment[field]).filter((item) => item !== value),
      },
    }));
  };

  const lookupPerson = async (kennitala) => {
    try {
      const cacheResponse = await axios.get(`${API}/kennitala-cache/${kennitala}`);
      return cacheResponse.data;
    } catch (cacheError) {
      const response = await axios.post(`${API}/creditinfo/lookup-person`, { kennitala });
      return response.data;
    }
  };

  const handlePartyLookup = async (group, key, target = "party") => {
    const party = form[group][key];
    const kennitala = target === "secondary" ? party.secondarySigner?.kennitala : party.kennitala;
    if (!kennitala || kennitala.length !== 10) {
      toast.error("Sláðu inn 10 stafa kennitölu");
      return;
    }

    const lookupKey = `${group}-${key}-${target}`;
    setLookupLoadingMap((current) => ({ ...current, [lookupKey]: true }));
    try {
      const person = await lookupPerson(kennitala);
      if (target === "secondary") {
        updateNestedGroup(group, key, {
          ...party,
          secondarySigner: {
            ...party.secondarySigner,
            kennitala,
            name: person?.name || party.secondarySigner?.name,
            phone: person?.phone ? person.phone.replace("+354", "") : party.secondarySigner?.phone,
            email: person?.email || party.secondarySigner?.email,
          },
        });
      } else {
        updateNestedGroup(group, key, personLookupToPartyFields({ ...person, kennitala }, party));
      }
      toast.success("Kennitöluupplýsingar sóttar");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Kennitöluuppfletting mistókst");
    } finally {
      setLookupLoadingMap((current) => ({ ...current, [lookupKey]: false }));
    }
  };

  const handleVehicleLookup = async () => {
    const fastanumer = (form.vehicle.fastanumer || form.vehicle.registrationNumber || "").trim().toUpperCase();
    if (!fastanumer || fastanumer.length < 3) {
      toast.error("Sláðu inn gilt bílnúmer");
      return;
    }

    setVehicleLookupLoading(true);
    setVehicleLookupFailed(false);
    setMatchedLoanText("");

    try {
      const [vehicleResult, loanOptionsResult] = await Promise.allSettled([
        axios.post(`${API}/vehicle-lookup`, { fastanumer }),
        axios.get(`${API}/afsals/loan-options`, { params: { query: fastanumer, limit: 5 } }),
      ]);

      let usedLoanPrefill = false;

      if (vehicleResult.status === "fulfilled") {
        setForm((current) => applyVehicleLookupToForm({
          ...current,
          vehicle: {
            ...current.vehicle,
            fastanumer,
            registrationNumber: fastanumer,
          },
        }, vehicleResult.value.data));
      }

      if (loanOptionsResult.status === "fulfilled") {
        const exactMatch = (loanOptionsResult.value.data || []).find(
          (item) => (item.registration_number || "").toUpperCase() === fastanumer,
        );

        if (exactMatch?.loan_id) {
          const loanResponse = await axios.get(`${API}/loans/${exactMatch.loan_id}`);
          setForm((current) => applyLoanToAfsolForm(current, loanResponse.data));
          setMatchedLoanText(`${exactMatch.borrower_name} · ${exactMatch.loan_number ? `Lán ${exactMatch.loan_number}` : "Ónúmerað lán"}`);
          usedLoanPrefill = true;
        }
      }

      if (vehicleResult.status === "fulfilled") {
        toast.success("Ökutæki fundið");
      } else if (usedLoanPrefill) {
        setVehicleLookupFailed(false);
        toast.success("Ökutækisupplýsingar sóttar úr lánagögnum");
      } else {
        setVehicleLookupFailed(true);
        toast.error(vehicleResult.reason?.response?.data?.detail || "Ökutækjauppfletting mistókst — handvirk skráning virk");
      }
    } catch (error) {
      setVehicleLookupFailed(true);
      toast.error("Ekki tókst að klára ökutækjaleit");
    } finally {
      setVehicleLookupLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        loan_id: form.general.loanId || undefined,
        loan_number: form.general.loanNumber ? Number(form.general.loanNumber) : undefined,
        reference_number: form.general.referenceNumber ? Number(form.general.referenceNumber) : undefined,
        registration_number: form.vehicle.fastanumer || form.vehicle.registrationNumber,
        vehicle_type: form.vehicle.displayName || `${form.vehicle.manufacturer} ${form.vehicle.model}`.trim(),
        afsal_date: form.general.afsalDate,
        notes: form.general.comments,
        seller_name: form.seller.owner.name,
        buyer_name: form.buyer.owner.name,
        buyer_kennitala: form.buyer.owner.kennitala,
        form_data: form,
      };

      const response = recordId
        ? await axios.put(`${API}/afsals/${recordId}`, payload)
        : await axios.post(`${API}/afsals`, payload);

      setRecordId(response.data.id);
      setForm(hydrateAfsolForm(response.data.form_data, defaultCreatedBy));
      toast.success("Afsal vistað");
      if (!recordId) {
        navigate(`/afsol/${response.data.id}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ekki tókst að vista afsal");
    } finally {
      setSaving(false);
    }
  };

  const handlePrintMode = (mode) => {
    const source = document.getElementById(`afsol-print-${mode}`);
    const printArea = document.getElementById("print-area");

    if (!source || !printArea) {
      toast.error("Ekki tókst að undirbúa prentútgáfu");
      return;
    }

    printArea.innerHTML = source.innerHTML;
    setTimeout(() => window.print(), 100);
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const printMode = params.get("print");

    if (!recordId || loading || !printMode || autoPrintHandledRef.current) {
      return;
    }

    if (printMode !== "electronic" && printMode !== "handwritten") {
      return;
    }

    autoPrintHandledRef.current = true;
    setTimeout(() => {
      handlePrintMode(printMode);
    }, 250);
  }, [loading, location.search, recordId]);

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#efefee]" data-testid="afsol-form-loading">
        <Loader2 className="mr-2 h-5 w-5 animate-spin text-slate-500" />
        <span className="text-sm text-slate-600">Hleð afsalsdrögum...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#efefee] px-3 py-4" data-testid="afsol-form-page">
      <div className="mx-auto max-w-[1700px] space-y-4">
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-slate-300 bg-white px-4 py-3">
          <div className="space-y-1">
            <Button type="button" variant="ghost" size="sm" onClick={() => navigate("/afsol")} className="h-8 px-2 text-slate-500" data-testid="afsol-back-button">
              <ArrowLeft className="h-4 w-4" />
              Til baka
            </Button>
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-3xl font-semibold text-slate-900" data-testid="afsol-form-title">{pageTitle}</h1>
              {form.general.loanNumber ? <Badge variant="outline" data-testid="afsol-loan-number-badge">Lán {form.general.loanNumber}</Badge> : null}
              {matchedLoanText ? <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100" data-testid="afsol-matched-loan-badge">{matchedLoanText}</Badge> : null}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <Button type="button" variant="outline" onClick={() => handlePrintMode("electronic")} data-testid="afsol-electronic-pdf-button">
              PDF fyrir rafræna undirritun
            </Button>
            <Button type="button" variant="outline" onClick={() => handlePrintMode("handwritten")} data-testid="afsol-handwritten-print-button">
              Prenta handundirritun
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate("/afsol")} data-testid="afsol-cancel-top-button">Hætta við</Button>
            <Button type="button" onClick={handleSave} disabled={saving} className="bg-[#1f6cc5] hover:bg-[#15579f]" data-testid="afsol-save-button">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
              Vista
            </Button>
          </div>
        </div>

        <div className="grid gap-4 xl:grid-cols-[1.1fr_2.3fr_1.7fr_1.7fr]">
          <AfsolSectionCard title="Almennt" testId="afsol-general-section">
            <div className="space-y-3">
              <SectionInput label="Raðnúmer" value={form.general.referenceNumber} onChange={(value) => updateSection("general", "referenceNumber", value.replace(/\D/g, ""))} testId="afsol-reference-number-input" readOnly={Boolean(recordId)} />
              <SectionInput label="Dagsetning afsals" type="date" value={form.general.afsalDate} onChange={(value) => updateSection("general", "afsalDate", value)} testId="afsol-date-input" />
              <SectionInput label="Bæjarfélag sem sala fer fram í" value={form.general.placeOfSale} onChange={(value) => updateSection("general", "placeOfSale", value)} testId="afsol-place-input" />
              <SectionInput label="Skráð af" value={form.general.createdBy} onChange={(value) => updateSection("general", "createdBy", value)} testId="afsol-created-by-input" />
              <div className="space-y-1">
                <Label htmlFor="afsol-general-comments" className="text-[11px] font-medium text-slate-600">Athugasemdir</Label>
                <Textarea id="afsol-general-comments" value={form.general.comments} onChange={(event) => updateSection("general", "comments", event.target.value)} className="min-h-[150px] rounded-sm border-slate-300 text-sm" data-testid="afsol-general-comments-textarea" />
              </div>
            </div>
          </AfsolSectionCard>

          <AfsolSectionCard title="Ökutæki" testId="afsol-vehicle-section">
            <div className="space-y-3">
              <div className="grid gap-3 lg:grid-cols-[1fr_1fr_auto]">
                <SectionInput label="Framleiðandi" value={form.vehicle.manufacturer} onChange={(value) => updateSection("vehicle", "manufacturer", value)} testId="afsol-vehicle-manufacturer-input" />
                <SectionInput label="Gerð" value={form.vehicle.model} onChange={(value) => updateSection("vehicle", "model", value)} testId="afsol-vehicle-model-input" />
                <Button type="button" variant="outline" onClick={handleVehicleLookup} disabled={vehicleLookupLoading} className="mt-6 h-9 rounded-sm border-slate-300" data-testid="afsol-vehicle-lookup-button">
                  {vehicleLookupLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>

              <div className="grid gap-3 md:grid-cols-3">
                <SectionInput label="Bílnúmer" value={form.vehicle.fastanumer} onChange={(value) => updateSection("vehicle", "fastanumer", value.toUpperCase())} testId="afsol-vehicle-fastanumer-input" />
                <SectionInput label="Skráningarnúmer" value={form.vehicle.registrationNumber} onChange={(value) => updateSection("vehicle", "registrationNumber", value.toUpperCase())} testId="afsol-vehicle-registration-number-input" />
                <SectionInput label="Verksmiðjunúmer" value={form.vehicle.vin} onChange={(value) => updateSection("vehicle", "vin", value.toUpperCase())} testId="afsol-vehicle-vin-input" />
              </div>

              <div className="grid gap-3 md:grid-cols-5">
                <SectionInput label="Fyrsta skráning" type="date" value={form.vehicle.firstRegistration} onChange={(value) => updateSection("vehicle", "firstRegistration", value)} testId="afsol-vehicle-first-registration-input" />
                <SectionInput label="Módelár" value={form.vehicle.modelYear} onChange={(value) => updateSection("vehicle", "modelYear", value)} testId="afsol-vehicle-model-year-input" />
                <SectionInput label="Framleiðsluár" value={form.vehicle.productionYear} onChange={(value) => updateSection("vehicle", "productionYear", value)} testId="afsol-vehicle-production-year-input" />
                <SectionInput label="Næsta aðalskoðun" value={form.vehicle.nextInspection} onChange={(value) => updateSection("vehicle", "nextInspection", value)} testId="afsol-vehicle-next-inspection-input" />
                <SectionInput label="Síðasta skoðun" value={form.vehicle.lastInspection} onChange={(value) => updateSection("vehicle", "lastInspection", value)} testId="afsol-vehicle-last-inspection-input" />
              </div>

              <div className="grid gap-3 md:grid-cols-[1fr_120px_120px_120px]">
                <div className="space-y-1">
                  <SectionInput label="Akstur skv. seljanda" value={form.vehicle.mileage} onChange={(value) => updateSection("vehicle", "mileage", value.replace(/[^\d.]/g, ""))} testId="afsol-vehicle-mileage-input" />
                  {form.vehicle.mileageSource === "creditinfo" && (
                    <span className="text-[10px] text-emerald-600 font-medium" data-testid="mileage-creditinfo-badge">Creditinfo</span>
                  )}
                </div>
                <div className="space-y-1">
                  <Label htmlFor="afsol-vehicle-mileage-unit-select" className="text-[11px] font-medium text-slate-600">Staða akstursmælis</Label>
                  <select id="afsol-vehicle-mileage-unit-select" value={form.vehicle.mileageUnit} onChange={(event) => updateSection("vehicle", "mileageUnit", event.target.value)} className="h-9 w-full rounded-sm border border-slate-300 px-3 text-sm" data-testid="afsol-vehicle-mileage-unit-select">
                    <option value="km.">km.</option>
                    <option value="mílur">mílur</option>
                  </select>
                </div>
                <SectionInput label="Lyklar með fjarstýringu" value={form.vehicle.keysWithRemote} onChange={(value) => updateSection("vehicle", "keysWithRemote", value.replace(/\D/g, ""))} testId="afsol-vehicle-keys-with-remote-input" />
                <SectionInput label="Lyklar án fjarstýringar" value={form.vehicle.keysWithoutRemote} onChange={(value) => updateSection("vehicle", "keysWithoutRemote", value.replace(/\D/g, ""))} testId="afsol-vehicle-keys-without-remote-input" />
              </div>

              {/* Km ferill frá Creditinfo — removed, latest reading goes into Akstur field */}

              <div className="flex flex-wrap gap-5 pt-1 text-sm text-slate-700">
                {[
                  ["isNewVehicle", "Nýtt ökutæki", "afsol-vehicle-is-new-checkbox"],
                  ["isVatVehicle", "VSK ökutæki", "afsol-vehicle-is-vat-checkbox"],
                  ["continueLeasing", "Ökutækið er og á að vera áfram í ökutækjaleigu", "afsol-vehicle-continue-leasing-checkbox"],
                ].map(([field, label, testId]) => (
                  <label key={String(field)} className="flex items-center gap-2">
                    <Checkbox checked={Boolean(form.vehicle[field])} onCheckedChange={(checked) => updateSection("vehicle", field, Boolean(checked))} data-testid={testId} />
                    {label}
                  </label>
                ))}
              </div>

              {vehicleLookupFailed ? (
                <div className="flex items-center gap-2 rounded-sm border border-orange-200 bg-orange-50 px-3 py-2 text-sm text-orange-700" data-testid="afsol-vehicle-manual-banner">
                  <CarFront className="h-4 w-4" />
                  Ökutækjauppfletting mistókst — fylltu reitina handvirkt.
                </div>
              ) : null}
            </div>
          </AfsolSectionCard>

          <AfsolSectionCard title="Ástand" testId="afsol-condition-section">
            <div className="space-y-3">
              <div className="grid gap-3 md:grid-cols-[auto_1fr_auto] md:items-end">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <Checkbox checked={Boolean(form.condition.temporaryRegistered)} onCheckedChange={(checked) => updateSection("condition", "temporaryRegistered", Boolean(checked))} data-testid="afsol-condition-temporary-registered-checkbox" />
                  Tímabundiður
                </label>
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <Checkbox checked={Boolean(form.condition.timingBeltChanged)} onCheckedChange={(checked) => updateSection("condition", "timingBeltChanged", Boolean(checked))} data-testid="afsol-condition-timing-belt-checkbox" />
                  Skipt út við
                </label>
                <SectionInput label="þ.km." value={form.condition.timingBeltMileage} onChange={(value) => updateSection("condition", "timingBeltMileage", value.replace(/\D/g, ""))} testId="afsol-condition-timing-belt-mileage-input" />
              </div>

              <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3 text-sm text-slate-700">
                {[
                  ["oilLeak", "Smurolíuleki", "afsol-condition-oil-leak-checkbox"],
                  ["oilBurn", "Smurolíubrunni", "afsol-condition-oil-burn-checkbox"],
                  ["coolantLeak", "Kælivökvaleki", "afsol-condition-coolant-leak-checkbox"],
                  ["majorDamage", "Burðarvirki og/eða stýrisbúnaður hefur raskast verulega", "afsol-condition-major-damage-checkbox"],
                  ["buyerInspected", "Kaupandi hefur látið ástandsskoða ökutækið", "afsol-condition-buyer-inspected-checkbox"],
                ].map(([field, label, testId]) => (
                  <label key={String(field)} className="flex items-center gap-2">
                    <Checkbox checked={Boolean(form.condition[field])} onCheckedChange={(checked) => updateSection("condition", field, Boolean(checked))} data-testid={testId} />
                    {label}
                  </label>
                ))}
              </div>

              <div className="space-y-1">
                <Label htmlFor="afsol-condition-comments" className="text-[11px] font-medium text-slate-600">Athugasemdir við ástandsskoðun</Label>
                <Textarea id="afsol-condition-comments" value={form.condition.conditionComments} onChange={(event) => updateSection("condition", "conditionComments", event.target.value)} className="min-h-[110px] rounded-sm border-slate-300 text-sm" data-testid="afsol-condition-comments-textarea" />
              </div>

              <div className="space-y-1">
                <Label htmlFor="afsol-condition-additional-info" className="text-[11px] font-medium text-slate-600">Nánari upplýsingar</Label>
                <Textarea id="afsol-condition-additional-info" value={form.condition.additionalInfo} onChange={(event) => updateSection("condition", "additionalInfo", event.target.value)} className="min-h-[110px] rounded-sm border-slate-300 text-sm" data-testid="afsol-condition-additional-info-textarea" />
              </div>
            </div>
          </AfsolSectionCard>

          <AfsolSectionCard title="Fylgihlutir" testId="afsol-accessories-section">
            <div className="space-y-4">
              <div className="grid gap-3 md:grid-cols-2 text-sm text-slate-700">
                {ACCESSORY_FIELDS.map((field) => (
                  <label key={field.key} className="flex items-center gap-2">
                    <Checkbox checked={Boolean(form.accessories[field.key])} onCheckedChange={(checked) => updateSection("accessories", field.key, Boolean(checked))} data-testid={`afsol-accessory-${field.key}-checkbox`} />
                    {field.label}
                  </label>
                ))}
              </div>
              <div className="space-y-1">
                <Label htmlFor="afsol-accessories-notes" className="text-[11px] font-medium text-slate-600">Nánari upplýsingar</Label>
                <Textarea id="afsol-accessories-notes" value={form.accessories.notes} onChange={(event) => updateSection("accessories", "notes", event.target.value)} className="min-h-[200px] rounded-sm border-slate-300 text-sm" data-testid="afsol-accessories-notes-textarea" />
              </div>
            </div>
          </AfsolSectionCard>
        </div>

        <div className="grid gap-4 xl:grid-cols-4">
          <AfsolSectionCard title="Tryggingar" testId="afsol-insurance-section">
            <div className="space-y-3">
              <div className="space-y-1">
                <Label htmlFor="afsol-insurance-seller-input" className="text-[11px] font-medium text-slate-600">Tryggingafélag seljanda</Label>
                <select
                  id="afsol-insurance-seller-input"
                  value={form.insurance.sellerInsurer || ""}
                  onChange={(event) => updateSection("insurance", "sellerInsurer", event.target.value)}
                  className="h-9 w-full rounded-sm border border-slate-300 px-3 text-sm"
                  data-testid="afsol-insurance-seller-input"
                >
                  {INSURANCE_OPTIONS.map((option) => (
                    <option key={option || "blank"} value={option}>
                      {option || " "}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="afsol-insurance-buyer-input" className="text-[11px] font-medium text-slate-600">Tryggingafélag kaupanda</Label>
                <select
                  id="afsol-insurance-buyer-input"
                  value={form.insurance.buyerInsurer || ""}
                  onChange={(event) => updateSection("insurance", "buyerInsurer", event.target.value)}
                  className="h-9 w-full rounded-sm border border-slate-300 px-3 text-sm"
                  data-testid="afsol-insurance-buyer-input"
                >
                  {INSURANCE_OPTIONS.map((option) => (
                    <option key={option || "blank"} value={option}>
                      {option || " "}
                    </option>
                  ))}
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700">
                <Checkbox checked={Boolean(form.insurance.liabilityInsurance)} onCheckedChange={(checked) => updateSection("insurance", "liabilityInsurance", Boolean(checked))} data-testid="afsol-insurance-liability-checkbox" />
                Ábyrgðartrygging
              </label>
              <div className="grid gap-3 md:grid-cols-[auto_1fr] md:items-end">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <Checkbox checked={Boolean(form.insurance.cascoInsurance)} onCheckedChange={(checked) => updateSection("insurance", "cascoInsurance", Boolean(checked))} data-testid="afsol-insurance-casco-checkbox" />
                  Kaskótrygging - Eigin áhætta
                </label>
                <SectionInput label="kr." value={form.insurance.selfRisk} onChange={(value) => updateSection("insurance", "selfRisk", value.replace(/[^\d.]/g, ""))} testId="afsol-insurance-self-risk-input" />
              </div>
            </div>
          </AfsolSectionCard>

          <AfsolSectionCard title="Ábyrgðir" testId="afsol-warranties-section">
            <div className="space-y-3">
              <div className="grid gap-3 md:grid-cols-[1fr_1fr_120px] md:items-end">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <Checkbox checked={Boolean(form.warranties.brokerWarrantyValidUntil)} onCheckedChange={(checked) => updateSection("warranties", "brokerWarrantyValidUntil", checked ? normalizeIsoDate(form.general.afsalDate) : "")} data-testid="afsol-warranties-broker-checkbox" />
                  Ábyrgð umboðs gildir til
                </label>
                <SectionInput label="dagsetning" type="date" value={normalizeIsoDate(form.warranties.brokerWarrantyValidUntil)} onChange={(value) => updateSection("warranties", "brokerWarrantyValidUntil", value)} testId="afsol-warranties-valid-until-input" />
                <SectionInput label="þ.km." value={form.warranties.brokerWarrantyValidKm} onChange={(value) => updateSection("warranties", "brokerWarrantyValidKm", value.replace(/\D/g, ""))} testId="afsol-warranties-valid-km-input" />
              </div>
              <SectionInput label="gegn viðhaldseftirliti á hverja" value={form.warranties.rustWarrantyEveryKm} onChange={(value) => updateSection("warranties", "rustWarrantyEveryKm", value.replace(/\D/g, ""))} testId="afsol-warranties-rust-every-km-input" />
              <div className="space-y-1">
                <Label htmlFor="afsol-warranties-other" className="text-[11px] font-medium text-slate-600">Aðrar ábyrgðir</Label>
                <Textarea id="afsol-warranties-other" value={form.warranties.otherWarranties} onChange={(event) => updateSection("warranties", "otherWarranties", event.target.value)} className="min-h-[160px] rounded-sm border-slate-300 text-sm" data-testid="afsol-warranties-other-textarea" />
              </div>
            </div>
          </AfsolSectionCard>

          <AfsolSectionCard title="Skattar" testId="afsol-taxes-section">
            <div className="space-y-3 text-sm text-slate-700">
              <div className="grid gap-3 md:grid-cols-2">
                <label className="flex items-center gap-2"><Checkbox checked={Boolean(form.taxes.vehicleTaxPaid)} onCheckedChange={(checked) => updateSection("taxes", "vehicleTaxPaid", Boolean(checked))} data-testid="afsol-taxes-vehicle-paid-checkbox" />Bifreiðagjald greitt</label>
                <label className="flex items-center gap-2"><Checkbox checked={Boolean(form.taxes.weightTaxPaid)} onCheckedChange={(checked) => updateSection("taxes", "weightTaxPaid", Boolean(checked))} data-testid="afsol-taxes-weight-paid-checkbox" />Þungaskattur greiddur</label>
              </div>
              <label className="flex items-center gap-2"><Checkbox checked={Boolean(form.taxes.debtCollectionFee)} onCheckedChange={(checked) => updateSection("taxes", "debtCollectionFee", Boolean(checked))} data-testid="afsol-taxes-debt-collection-checkbox" />Bílasala innheimtir lögbundin gjöld</label>
              <div className="grid gap-3 md:grid-cols-2">
                <SectionInput label="Seljandi greiðir bifreiðagjald" value={form.taxes.sellerVehicleTax} onChange={(value) => updateSection("taxes", "sellerVehicleTax", value.replace(/[^\d.]/g, ""))} testId="afsol-taxes-seller-vehicle-input" />
                <SectionInput label="Seljandi greiðir þungaskatt" value={form.taxes.sellerWeightTax} onChange={(value) => updateSection("taxes", "sellerWeightTax", value.replace(/[^\d.]/g, ""))} testId="afsol-taxes-seller-weight-input" />
                <SectionInput label="Kaupandi greiðir bifreiðagjald" value={form.taxes.buyerVehicleTax} onChange={(value) => updateSection("taxes", "buyerVehicleTax", value.replace(/[^\d.]/g, ""))} testId="afsol-taxes-buyer-vehicle-input" />
                <SectionInput label="Kaupandi greiðir þungaskatt" value={form.taxes.buyerWeightTax} onChange={(value) => updateSection("taxes", "buyerWeightTax", value.replace(/[^\d.]/g, ""))} testId="afsol-taxes-buyer-weight-input" />
              </div>
            </div>
          </AfsolSectionCard>

          <AfsolSectionCard title="Fylgiskjöl" testId="afsol-attachments-section">
            <div className="space-y-4 text-sm text-slate-700">
              <div className="grid gap-3 md:grid-cols-2">
                {ATTACHMENT_FIELDS.map((field) => (
                  <label key={field.key} className="flex items-center gap-2">
                    <Checkbox checked={Boolean(form.attachments[field.key])} onCheckedChange={(checked) => updateSection("attachments", field.key, Boolean(checked))} data-testid={`afsol-attachment-${field.key}-checkbox`} />
                    {field.label}
                  </label>
                ))}
              </div>
              <div className="grid gap-3 md:grid-cols-[auto_1fr] md:items-end">
                <label className="flex items-center gap-2 text-sm text-slate-700">
                  <Checkbox checked={Boolean(form.attachments.webhandoverCompleted)} onCheckedChange={(checked) => updateSection("attachments", "webhandoverCompleted", Boolean(checked))} data-testid="afsol-attachments-webhandover-checkbox" />
                  Vefhandaútfyllt dagsett
                </label>
                <SectionInput label="Dagsetning" type="date" value={form.attachments.webhandoverDate} onChange={(value) => updateSection("attachments", "webhandoverDate", value)} testId="afsol-attachments-webhandover-date-input" />
              </div>
            </div>
          </AfsolSectionCard>
        </div>

        <AfsolSectionCard title="Greiðslufyrirkomulag" testId="afsol-payment-section">
          <div className="space-y-4">
            <div className="grid gap-4 md:grid-cols-[minmax(260px,420px)_1fr]">
              <SectionInput label="Kaupverð" value={form.payment.purchasePrice} onChange={(value) => updateSection("payment", "purchasePrice", value.replace(/[^\d.]/g, ""))} testId="afsol-payment-purchase-price-input" />
              <div className="grid gap-3 md:grid-cols-4">
                {form.payment.breakdown.map((item, index) => (
                  <SectionInput key={item.label} label={item.label} value={item.amount} onChange={(value) => updatePaymentBreakdown(index, value.replace(/[^\d.]/g, ""))} testId={`afsol-payment-breakdown-${index}-input`} />
                ))}
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="afsol-payment-comments" className="text-[11px] font-medium text-slate-600">Athugasemdir</Label>
              <Textarea id="afsol-payment-comments" value={form.payment.comments} onChange={(event) => updateSection("payment", "comments", event.target.value)} className="min-h-[120px] rounded-sm border-slate-300 text-sm" data-testid="afsol-payment-comments-textarea" />
            </div>

            <div className="grid gap-4 xl:grid-cols-2">
              {[{ key: "sellerFinancingNotes", title: "Upplýsingar fjármögnun seljanda" }, { key: "buyerFinancingNotes", title: "Upplýsingar fjármögnun kaupanda" }].map((block) => (
                <div key={block.key} className="space-y-2 rounded-lg border border-slate-300 bg-white p-3" data-testid={`afsol-payment-${block.key}-section`}>
                  <p className="text-sm font-semibold text-slate-700">{block.title}</p>
                  <select
                    value={financingSelection[block.key] || ""}
                    onChange={(event) => addFinancingSource(block.key, event.target.value)}
                    className="h-9 w-full rounded-sm border border-slate-300 px-3 text-sm"
                    data-testid={`afsol-payment-${block.key}-source-select`}
                  >
                    {FINANCING_SOURCE_OPTIONS.map((option) => (
                      <option key={option || "blank"} value={option}>
                        {option || " "}
                      </option>
                    ))}
                  </select>
                  <div className="min-h-[86px] rounded-sm border border-slate-300 bg-slate-50 p-2" data-testid={`afsol-payment-${block.key}-selected-box`}>
                    <div className="flex flex-wrap gap-2">
                      {normalizeFinancingSources(form.payment[block.key]).length > 0 ? normalizeFinancingSources(form.payment[block.key]).map((item) => (
                        <button
                          key={`${block.key}-${item}`}
                          type="button"
                          onClick={() => removeFinancingSource(block.key, item)}
                          className="rounded-full border border-slate-300 bg-white px-3 py-1 text-xs text-slate-700 hover:bg-slate-100"
                          data-testid={`afsol-payment-${block.key}-${item}-chip`}
                        >
                          {item} ×
                        </button>
                      )) : <span className="text-xs text-slate-400">Veldu einn eða fleiri flokka úr listanum hér að ofan.</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </AfsolSectionCard>

        <section className="space-y-3" data-testid="afsol-seller-section">
          <h2 className="text-3xl font-semibold text-slate-900">Seljandi</h2>
          <div className="grid gap-4 xl:grid-cols-3">
            <AfsolPartyPanel prefix="seller-owner" title="Eigandi" party={form.seller.owner} onChange={(value) => updateNestedGroup("seller", "owner", value)} onLookup={(target) => handlePartyLookup("seller", "owner", target)} lookupLoading={Boolean(lookupLoadingMap["seller-owner-party"] || lookupLoadingMap["seller-owner-secondary"])} showNotificationToggle />
            <AfsolPartyPanel prefix="seller-co-owner" title="Meðeigandi / Umráðamaður" party={form.seller.coOwner} onChange={(value) => updateNestedGroup("seller", "coOwner", value)} onLookup={(target) => handlePartyLookup("seller", "coOwner", target)} lookupLoading={Boolean(lookupLoadingMap["seller-coOwner-party"] || lookupLoadingMap["seller-coOwner-secondary"])} showRoleTabs />
            <AfsolPartyPanel prefix="seller-keeper" title="Umráðamaður" party={form.seller.keeper} onChange={(value) => updateNestedGroup("seller", "keeper", value)} onLookup={(target) => handlePartyLookup("seller", "keeper", target)} lookupLoading={Boolean(lookupLoadingMap["seller-keeper-party"] || lookupLoadingMap["seller-keeper-secondary"])} />
          </div>
        </section>

        <section className="space-y-3" data-testid="afsol-buyer-section">
          <h2 className="text-3xl font-semibold text-slate-900">Kaupandi</h2>
          <div className="grid gap-4 xl:grid-cols-3">
            <AfsolPartyPanel prefix="buyer-owner" title="Eigandi" party={form.buyer.owner} onChange={(value) => updateNestedGroup("buyer", "owner", value)} onLookup={(target) => handlePartyLookup("buyer", "owner", target)} lookupLoading={Boolean(lookupLoadingMap["buyer-owner-party"] || lookupLoadingMap["buyer-owner-secondary"])} />
            <AfsolPartyPanel prefix="buyer-co-owner" title="Meðeigandi / Umráðamaður" party={form.buyer.coOwner} onChange={(value) => updateNestedGroup("buyer", "coOwner", value)} onLookup={(target) => handlePartyLookup("buyer", "coOwner", target)} lookupLoading={Boolean(lookupLoadingMap["buyer-coOwner-party"] || lookupLoadingMap["buyer-coOwner-secondary"])} showRoleTabs />
            <AfsolPartyPanel prefix="buyer-keeper" title="Umráðamaður" party={form.buyer.keeper} onChange={(value) => updateNestedGroup("buyer", "keeper", value)} onLookup={(target) => handlePartyLookup("buyer", "keeper", target)} lookupLoading={Boolean(lookupLoadingMap["buyer-keeper-party"] || lookupLoadingMap["buyer-keeper-secondary"])} />
          </div>
        </section>

        <div className="hidden">
          <div id="afsol-print-electronic">
            <AfsolPrintDocument form={form} mode="electronic" />
          </div>
          <div id="afsol-print-handwritten">
            <AfsolPrintDocument form={form} mode="handwritten" />
          </div>
        </div>
      </div>
    </div>
  );
};

export default AfsolFormPage;